import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Upload, ScanLine, Calculator, Ruler, Sparkles, Copy, Camera, ReceiptText, Bot, MessageSquareText } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import ReviewBadges from "@/components/ReviewBadges";
import { Button } from "@/components/ui/button";

const SITE = "https://tradeoff.blueshuckconsulting.com";
const HERO_IMG = "https://images.pexels.com/photos/4458205/pexels-photo-4458205.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const STEPS = [
  { icon: Upload, t: "Upload your drawing", d: "Drop in a PDF or scanned plan — or snap a photo of a drawing or schedule on your phone on site." },
  { icon: ScanLine, t: "AI reads it", d: "TradeOff detects the scale, pulls quantities from schedules and legends, and auto-counts repeated symbols." },
  { icon: Calculator, t: "Priced estimate back", d: "Extracted quantities are matched to your rate library and priced for your region — review every number, then bill." },
];

const FEATURES = [
  { icon: Ruler, t: "Automatic scale detection", d: "AI reads the title-block scale (e.g. 1:100) so you can measure straight away — no fiddling with calibration first." },
  { icon: Sparkles, t: "Schedule & legend extraction", d: "Door, window, sanitaryware and finishes schedules are read into structured quantities you can drop into your estimate." },
  { icon: Copy, t: "Symbol auto-count", d: "Box one socket, light or fitting and AI finds every match across the sheet — no more manual tallying." },
  { icon: Bot, t: "AI rate suggestions", d: "Each line is matched to your rate library with a confidence score, then adjusted to your region before you send." },
  { icon: MessageSquareText, t: "Ask the drawing", d: "Ask questions in plain English — \"how many fire doors?\", \"what's the floor area?\" — and get an answer with its source, then push it straight into your estimate." },
  { icon: Camera, t: "Snap a drawing on your phone", d: "On site with no laptop? Photograph a drawing or schedule and get quantities back in seconds." },
  { icon: ReceiptText, t: "Straight to billing", d: "Turn the priced estimate into a CIS- and VAT-reverse-charge-ready Application for Payment and get paid." },
];

const FAQS = [
  { q: "What is AI takeoff software?", a: "AI takeoff software reads your construction drawings automatically instead of you measuring everything by hand. TradeOff detects the drawing scale, extracts quantities from schedules and legends, and counts repeated symbols — giving you a first-pass takeoff in seconds that you review and refine." },
  { q: "How accurate is the AI takeoff?", a: "TradeOff's AI is a fast first pass, not a black box. Every quantity, count and suggested rate is shown for you to review and edit before it hits your estimate, so you stay in control of the numbers you send to a client." },
  { q: "Can AI price my estimate too?", a: "Yes. Extracted quantities are matched to your own rate library with a confidence score and adjusted to your pricing region, so you get a priced estimate — not just quantities — that you can tune before sending." },
  { q: "Do I need to calibrate the scale manually?", a: "Usually not. AI reads the scale from the title block (e.g. 1:100). You can always override it, but for most drawings you can start measuring straight away." },
  { q: "Can I do a takeoff from my phone?", a: "Yes. Snap a photo of a drawing or schedule on site and TradeOff's AI returns quantities you can turn into a priced estimate — handy when you're on the job without a laptop." },
  { q: "Is it built for UK subcontractors?", a: "Yes. TradeOff is made for UK subcontractors — AI takeoff and estimating flow straight into Applications for Payment with CIS deductions, VAT domestic reverse charge and Xero sync built in." },
];

const STRUCTURED_DATA = [
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "TradeOff — AI Takeoff Software",
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "AI Construction Takeoff & Estimating Software",
    operatingSystem: "Web",
    url: `${SITE}/ai-takeoff`,
    description: "AI takeoff software for UK subcontractors. Upload a PDF drawing and AI detects the scale, extracts quantities from schedules and counts symbols — then prices it from your rate library.",
    offers: { "@type": "Offer", price: "0", priceCurrency: "GBP", description: "Free plan — no credit card required" },
    publisher: { "@type": "Organization", name: "Blue Shuck Consulting Ltd", url: "https://www.blueshuckconsulting.com" },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
];

const AiTakeoff = () => {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const go = () => navigate("/register");

  return (
    <div className="bg-white" data-testid="ai-takeoff-page">
      <Seo
        title="AI Takeoff Software for UK Subcontractors — Measure & Price from PDF Drawings"
        description="TradeOff is AI takeoff software for UK subcontractors. Upload a PDF or scanned drawing and AI detects the scale, extracts quantities from schedules and auto-counts symbols — then prices it from your rate library and bills with CIS & VAT reverse charge. Start free, no card."
        path="/ai-takeoff"
        jsonLd={STRUCTURED_DATA}
      />
      <MarketingNav />

      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 grid-bg opacity-70" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
              <Sparkles className="h-3.5 w-3.5" /> AI takeoff
            </span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.08]">
              AI takeoff software that reads your drawings
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl">
              Upload a PDF or scanned plan and TradeOff's AI detects the scale, extracts quantities from schedules and counts symbols — then prices it from your rate library. A takeoff that used to take hours takes minutes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={go} data-testid="ai-hero-cta" className="bg-blue-600 hover:bg-blue-700 h-12 px-7 text-base">
                Run your first AI takeoff free <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/pricing")} className="h-12 px-7 text-base border-slate-300">See pricing</Button>
            </div>
            <p className="mt-4 text-sm text-slate-500">No credit card required · Built for UK subcontractors</p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-lg"
          >
            <img src={HERO_IMG} alt="AI takeoff reading a construction drawing" className="w-full h-80 object-cover" />
            <span className="absolute bottom-3 left-3 bg-white/90 backdrop-blur text-xs font-medium text-slate-700 px-2.5 py-1 rounded-md">From drawing → priced estimate</span>
          </motion.div>
        </div>
      </section>

      <section className="py-20" data-testid="ai-steps">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">From drawing to priced estimate in three steps</h2>
            <p className="mt-3 text-slate-600">You stay in control — every AI number is yours to review before it reaches a client.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.t}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="relative rounded-xl border border-slate-200 bg-slate-50/50 p-6"
                data-testid={`ai-step-${i}`}
              >
                <span className="absolute top-5 right-5 text-4xl font-display font-bold text-slate-200">{i + 1}</span>
                <div className="h-11 w-11 rounded-lg bg-blue-600/10 flex items-center justify-center"><s.icon className="h-5 w-5 text-blue-600" /></div>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="ai-features">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">What the AI does for you</h2>
            <p className="mt-3 text-slate-600">Purpose-built for UK construction drawings and the way subcontractors actually work.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((c, i) => (
              <motion.div
                key={c.t}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-xl border border-slate-200 bg-white p-6"
                data-testid={`ai-feature-${i}`}
              >
                <div className="h-11 w-11 rounded-lg bg-blue-600/10 flex items-center justify-center"><c.icon className="h-5 w-5 text-blue-600" /></div>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{c.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.d}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center"><ReviewBadges /></div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="ai-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 text-center">AI takeoff — common questions</h2>
          <div className="mt-10 space-y-4">
            {FAQS.map((f, i) => (
              <div key={f.q} className="rounded-xl border border-slate-200 p-5" data-testid={`ai-faq-${i}`}>
                <h3 className="font-display text-base font-semibold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" style={{ backgroundColor: "#16305C" }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Run your first AI takeoff in minutes</h2>
          <p className="mt-4 text-slate-300">Start free — upload a drawing and let TradeOff's AI do the first pass for you.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button size="lg" onClick={go} data-testid="ai-cta-btn" className="bg-blue-600 hover:bg-blue-500 h-12 px-8 text-base">
              Create your workspace <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-300">
            {["AI takeoff", "One-click AI pricing", "CIS & VAT reverse charge", "Xero sync"].map((f) => (
              <span key={f} className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-blue-400" />{f}</span>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AiTakeoff;
